import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom, Observable, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { environment } from 'oneok/environments/environment';
import type { ServiceNowUnsubscribeRequest, TPServiceRequest } from '@legacy-models/target-process/TPServiceRequest';
import type { ServiceNowIncident } from '@legacy-models/service-now/ServiceNowIncident';
import { Contact } from '@legacy-models/portal/Contact';

@Injectable({
  providedIn: 'root'
})
export class Utility {
  readonly baseUrl = environment.apiUrlV2;
  tpUrl = '';
  private readonly _http = inject(HttpClient);
  testUser: Contact = {
    Id: '701cc249776fea118137005056b80a99',
    Phone: '',
    Mobile: '',
    Email: 'test.user@oneok.com',
    FirstName: 'Test',
    LastName: 'Tester',
    IsEmployee: false,
    BusinessSegment: 'NGGP',
    Status: 'Active',
    RegisteredStatus: true,
    LoginEnabled: true,
    EmailConfirmed: true,
    PortalRoles: [],
    SignedAdminForm: true,
    DocuSignEnvelopeId: 'e20fe731-4451-4b30-82fd-55e465c93366'
  };
  testAcct: unknown;
  shortDescriptionFields = ['accountName', 'requestType', 'assetAreas'];
  descriptionFields = ['requesterEmail', 'userName', 'businessSegment', 'comments'];

  constructor() {
    const appUrl = window.location.href;
    const isLocal = appUrl.indexOf('localhost:') >= 0;
    const isDev = appUrl.indexOf('dynlab.oneok.com') >= 0;
    const isTest = appUrl.indexOf('dyntest.oneok.com') >= 0;
    const targetProcessConfig = environment.targetProcess as {
      url?: string;
      urlDev?: string;
      urlQa?: string;
      urlTest?: string;
      urlProd?: string;
    };

    if (environment.production) {
      this.tpUrl = targetProcessConfig.url ?? '';
    } else if (isLocal) {
      this.tpUrl = targetProcessConfig.urlDev ?? '';
    } else if (isDev) {
      this.tpUrl = targetProcessConfig.url ?? '';
    } else if (isTest) {
      this.tpUrl = targetProcessConfig.url ?? '';
    }
  }

  formatAccountName(val: string): string {
    const arrVal: string[] = [];
    const strFormatted: string[] = [];
    const strProtected = new Set(['ONEOK', 'L.L.C', 'LLC', 'LP', 'PO', 'BP', 'EOG', 'XTO']);

    if (!val) {
      return '';
    }

    const analyzeStr = (str: string): void => {
      if (!str) {
        return;
      }
      if (strProtected.has(str) || str.length === 1 || str.indexOf('&') >= 0) {
        strFormatted.push(str);
      } else {
        strFormatted.push(str[0].toUpperCase() + str.substring(1).toLowerCase());
      }
    };

    arrVal.push(...val.split(' '));
    arrVal.forEach(analyzeStr);
    return strFormatted.join(' ');
  }

  // Post "unsubscribe" request to ServiceNow via /unsubscribe view
  postSNUnsubscribeRequest(serviceNowUnsubscribeRequest: ServiceNowUnsubscribeRequest): Promise<string> {
    const normalizedRequest: ServiceNowUnsubscribeRequest = {
      ...serviceNowUnsubscribeRequest,
      accountName: this.formatAccountName(serviceNowUnsubscribeRequest.accountName)
    };
    const { contactId } = normalizedRequest;
    const assetAreas = normalizedRequest.assetAreas as Record<string, string[]>;
    const serviceNowIncident: ServiceNowIncident = {
      Description: Object.entries(normalizedRequest)
        .map(([key, value]) => {
          if (this.descriptionFields.includes(key)) {
            return `${key}: ${value}`;
          }
          return undefined;
        })
        .filter((entry): entry is string => !!entry)
        .join('\n'),
      ShortDescription: Object.entries(normalizedRequest)
        .map(([key, value]) => {
          if (this.shortDescriptionFields.includes(key)) {
            const isNotAssetArea = key !== 'assetAreas';
            const isAssetArea = key === 'assetAreas' && Object.keys(assetAreas).length > 0;

            if (isNotAssetArea) {
              return `${key}: ${value}`;
            }
            if (isAssetArea) {
              const comments: string[] = [];
              for (const team in assetAreas) {
                if (Object.hasOwn(assetAreas, team)) {
                  comments.push(assetAreas[team].join(' & '));
                }
              }
              return `${key}:${comments.join('|')} `;
            }
          }
          return undefined;
        })
        .filter((entry): entry is string => !!entry)
        .join('|')
    };

    return lastValueFrom(
      this._http
        .post(this.baseUrl + '/servicenow/contacts/' + contactId + '/notifications/unsubscribe', serviceNowIncident, {
          responseType: 'text'
        })
        .pipe(
          catchError(error => throwError(() => error)),
          take(1)
        )
    );
  }

  // Post help center request to ServiceNow
  postSNRequest(tpServiceRequest: TPServiceRequest): Observable<string> {
    const normalizedRequest: TPServiceRequest = {
      ...tpServiceRequest,
      accountName: this.formatAccountName(tpServiceRequest.accountName)
    };
    const assetAreas = normalizedRequest.assetAreas as Record<string, string[]>;
    const descriptionData = Object.entries(normalizedRequest)
      .map(([key, value]) => {
        if (this.descriptionFields.includes(key)) {
          return `${key}: ${value}`;
        }
        return undefined;
      })
      .filter((entry): entry is string => !!entry);
    const shortDescriptionData = Object.entries(normalizedRequest)
      .map(([key, value]) => {
        if (this.shortDescriptionFields.includes(key)) {
          if (key !== 'assetAreas') {
            return `${key}: ${value}`;
          }
          if (key === 'assetAreas' && Object.keys(assetAreas).length) {
            const comments: string[] = [];
            for (const team in assetAreas) {
              if (Object.hasOwn(assetAreas, team)) {
                comments.push(assetAreas[team].join(' & '));
              }
            }
            return `${key}:${comments.join('|')} `;
          }
        }
        return undefined;
      })
      .filter((entry): entry is string => !!entry);
    const serviceNowIncData: ServiceNowIncident = {
      Description: descriptionData.join('\n'),
      ShortDescription: shortDescriptionData.join('|')
    };

    return this._http
      .post(this.baseUrl + '/servicenow/incident', serviceNowIncData, {
        responseType: 'text'
      })
      .pipe(
        catchError(error => throwError(() => error)),
        take(1)
      );
  }

  scrollToBottomOfView(): void {
    const divElement = document.getElementById('app-main');
    if (!divElement) {
      return;
    }

    divElement.scrollTop = divElement.scrollHeight;
    divElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }
}
